/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useCallback, useEffect, useState } from 'react';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import * as KeyCodes from 'UIComponents/constants/KeyCodes';
import QuizQuestionContainer from './QuizQuestionContainer';
import QuizEndScreen from './QuizEndScreen';
import QuizTopbar from '../../../components/QuizTopbar';
import QuizError from './QuizError';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import QuizControlBar from './QuizControlBar';
import { DEFAULT_LANGUAGE } from '../../../constants/ApiConstants';
import { Link } from '../../../components/AcademyLinks';
import { useContentStatus, useUpdateContentProgress } from '../../userProgress';
import { useClearQuizAttempt, useFinishQuiz, useQuiz, useRecentQuizAttempt, useStartQuiz, useSubmitAnswer } from '..';
import { useUnmountLifecycle } from '../../../hooks/useUnmountLifecycle';
import UsageTracker from '../../../utils/UsageTracker';
import { Metrics } from '../../../Metrics';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const QuizContainer = ({
  lessonId: lessonIdString,
  contentId,
  trackId,
  onFinish,
  language
}) => {
  const quizId = Number(contentId);
  const lessonId = Number(lessonIdString);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswerIds, setSelectedAnswerIds] = useState([]);
  const {
    contentStatus,
    contentStatusError,
    contentStatusLoading
  } = useContentStatus(quizId);
  const {
    recentQuizAttempt,
    recentQuizAttemptError,
    recentQuizAttemptLoading
  } = useRecentQuizAttempt(lessonId, quizId);
  const {
    quiz,
    quizError,
    quizLoading
  } = useQuiz(lessonId, quizId, language);
  const [startQuiz, {
    startQuizLoading,
    startQuizError
  }] = useStartQuiz();
  const [submitQuizAnswer, {
    submitQuizAnswerLoading,
    submitQuizAnswerError
  }] = useSubmitAnswer();
  const [finishQuiz, {
    finishQuizLoading,
    finishQuizError
  }] = useFinishQuiz();
  const [updateContentProgress, {
    updateContentProgressLoading,
    updateContentProgressError
  }] = useUpdateContentProgress();
  const clearQuizAttempt = useClearQuizAttempt();
  const contentLanguage = quiz && quiz.language || DEFAULT_LANGUAGE;
  const questions = quiz && quiz.questions;
  const hasCompleted = contentStatus && contentStatus === 'COMPLETED';
  const maybeAnswerSubmissions = recentQuizAttempt && recentQuizAttempt.answerSubmissions;
  const currentAttemptId = recentQuizAttempt && recentQuizAttempt.id;
  const currentQuestion = questions && questions[currentQuestionIndex];
  const error = contentStatusError || quizError || recentQuizAttemptError || startQuizError || submitQuizAnswerError || finishQuizError || updateContentProgressError;
  const loading = contentStatusLoading || recentQuizAttemptLoading || quizLoading || startQuizLoading || submitQuizAnswerLoading || finishQuizLoading || updateContentProgressLoading;
  const getHasAnsweredCurrentQuestion = useCallback(questionId => {
    return !!(maybeAnswerSubmissions && maybeAnswerSubmissions.find(submission => submission.questionId === questionId));
  }, [maybeAnswerSubmissions]);
  const isProgressButtonDisabled = !!error || !hasCompleted && (loading || selectedAnswerIds.length < 1);
  const finaliseAttempt = useCallback(() => {
    if (!currentAttemptId) return null;
    return finishQuiz(lessonId, quizId, currentAttemptId, trackId ? Number(trackId) : undefined, contentLanguage).then(() => {
      updateContentProgress({
        lessonId,
        contentId,
        language: contentLanguage
      });
      UsageTracker.track('interaction', {
        action: 'finish-quiz',
        lessonId,
        quizId,
        language: contentLanguage
      });
      Metrics.counter('quiz-finished', {
        language: contentLanguage
      }).increment();
    });
  }, [contentId, contentLanguage, currentAttemptId, finishQuiz, lessonId, quizId, trackId, updateContentProgress]);
  const advanceQuiz = useCallback(() => {
    if (!maybeAnswerSubmissions || !questions) return;
    const hasAnsweredAllQuestions = maybeAnswerSubmissions.length >= questions.length;
    if (!hasCompleted && maybeAnswerSubmissions.length > questions.length) {
      Raven.captureMessage(`QuizContainer: Bounds Error`, {
        extra: {
          currentAttemptId,
          attemptIsInitializing: startQuizLoading,
          attemptIsRequesting: recentQuizAttemptLoading,
          hasError: !!error,
          currentQuestionIndex,
          numAnswerSubmissions: maybeAnswerSubmissions.length,
          numQuestions: questions.length,
          maybeAnswerSubmissions: JSON.stringify(maybeAnswerSubmissions)
        }
      });
    }
    if (hasCompleted) {
      onFinish();
    } else if (hasAnsweredAllQuestions) {
      finaliseAttempt();
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswerIds([]);
    }
  }, [maybeAnswerSubmissions, questions, hasCompleted, currentAttemptId, startQuizLoading, recentQuizAttemptLoading, error, currentQuestionIndex, onFinish, finaliseAttempt]);
  const checkAnswer = useCallback(() => {
    if (!currentQuestion) return null;
    const questionId = currentQuestion.id;
    if (!currentAttemptId) {
      return startQuiz(lessonId, quizId, contentLanguage).then(attempt => {
        if (!attempt.data) return null;
        UsageTracker.track('interaction', {
          action: 'start-quiz',
          lessonId,
          quizId,
          language: contentLanguage
        });
        Metrics.counter('quiz-started', {
          language: contentLanguage
        }).increment();
        return submitQuizAnswer(lessonId, attempt.data.startQuiz.id, questionId, selectedAnswerIds).then(() => {
          UsageTracker.track('interaction', {
            action: 'answer-quiz-question',
            lessonId,
            quizId,
            language: contentLanguage
          });
          Metrics.counter('quiz-question-answered', {
            language: contentLanguage
          }).increment();
        });
      });
    } else {
      return submitQuizAnswer(lessonId, currentAttemptId, questionId, selectedAnswerIds).then(() => {
        UsageTracker.track('interaction', {
          action: 'answer-quiz-question',
          lessonId,
          quizId,
          language: contentLanguage
        });
        Metrics.counter('quiz-question-answered', {
          language: contentLanguage
        }).increment();
      });
    }
  }, [contentLanguage, currentAttemptId, currentQuestion, lessonId, quizId, selectedAnswerIds, startQuiz, submitQuizAnswer]);
  const handleKeydown = useCallback(event => {
    if (!currentQuestion) return;
    const hasAnsweredCurrentQuestion = getHasAnsweredCurrentQuestion(currentQuestion.id);
    const {
      target
    } = event;
    if (event.keyCode === KeyCodes.ENTER && isProgressButtonDisabled && !target.className.includes('ignore-enter-proceed')) {
      if (hasAnsweredCurrentQuestion) {
        advanceQuiz();
      } else {
        checkAnswer();
      }
    }
  }, [advanceQuiz, checkAnswer, currentQuestion, getHasAnsweredCurrentQuestion, isProgressButtonDisabled]);
  useUnmountLifecycle(() => {
    const numQuestions = questions ? questions.length : null;
    const answeredQuestions = maybeAnswerSubmissions ? maybeAnswerSubmissions.length : null;
    const answeredAllQuestions = !!numQuestions && !!answeredQuestions && numQuestions <= answeredQuestions;
    if (currentAttemptId && !answeredAllQuestions) {
      clearQuizAttempt(lessonId, quizId, currentAttemptId);
    }
  });
  useEffect(() => {
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [handleKeydown]);
  useEffect(() => {
    setCurrentQuestionIndex(0);
    setSelectedAnswerIds([]);
  }, [quizId]);
  const handleRetakeQuizClick = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswerIds([]);
    if (currentAttemptId) {
      clearQuizAttempt(lessonId, quizId, currentAttemptId);
      UsageTracker.track('interaction', {
        action: 'retake-quiz',
        lessonId,
        quizId,
        language: contentLanguage
      });
      Metrics.counter('quiz-restarted', {
        language: contentLanguage
      }).increment();
    }
  };
  const handleTryAgainClick = () => {
    setSelectedAnswerIds([]);
  };
  const handleSelectedAnswersChange = AnswerIds => {
    setSelectedAnswerIds(AnswerIds);
  };
  const hasAnsweredCurrentQuestion = !!currentQuestion && getHasAnsweredCurrentQuestion(currentQuestion.id);
  if (loading) {
    return /*#__PURE__*/_jsx("div", {
      className: "quiz-container",
      "data-test-id": "quiz-container",
      children: /*#__PURE__*/_jsx("div", {
        className: "quiz-container__quiz",
        children: /*#__PURE__*/_jsx(UILoadingSpinner, {
          className: "quiz-container__loading",
          grow: true
        })
      })
    });
  }
  if (error || !questions || !currentQuestion) {
    return /*#__PURE__*/_jsx("div", {
      className: "quiz-container",
      "data-test-id": "quiz-container",
      children: /*#__PURE__*/_jsx("div", {
        className: "quiz-container__quiz",
        children: /*#__PURE__*/_jsx(QuizError, {
          progressToNextContent: onFinish
        })
      })
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    className: "quiz-container",
    "data-test-id": "quiz-container",
    children: [/*#__PURE__*/_jsx("div", {
      className: "quiz-container__quiz",
      children: hasCompleted ? /*#__PURE__*/_jsx(QuizEndScreen, {
        className: "quiz-container__end-screen",
        correctAnswers: (maybeAnswerSubmissions || []).filter(answerSubmission => answerSubmission.correctAnswer).length,
        onRetakeClick: handleRetakeQuizClick,
        totalQuestions: questions.length
      }) : /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(QuizTopbar, {
          className: "quiz-container__topbar",
          currentQuestion: currentQuestionIndex + 1,
          numQuestions: questions.length,
          topRightComponent: /*#__PURE__*/_jsx(Link, {
            onClick: onFinish,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.quiz.skipQuiz"
            })
          })
        }), /*#__PURE__*/_jsx(QuizQuestionContainer, {
          lessonId: lessonId,
          onSelectAnswers: handleSelectedAnswersChange,
          onTryAgainClick: handleTryAgainClick,
          question: currentQuestion,
          quizId: quizId,
          selectedAnswerIds: selectedAnswerIds
        })]
      })
    }), /*#__PURE__*/_jsx(QuizControlBar, {
      buttonDisabled: isProgressButtonDisabled,
      hasAnsweredCurrentQuestion: hasAnsweredCurrentQuestion,
      onClick: hasAnsweredCurrentQuestion ? advanceQuiz : checkAnswer
    })]
  });
};
export default QuizContainer;